import React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Plus, 
  Image as ImageIcon,
  Video,
  ArrowUp,
  ArrowDown,
  Layers
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { CarouselCard } from "@/types/bulkAd";
import { toast } from "@/hooks/use-toast";

interface CreativeAsset {
  id: string;
  filename: string;
  meta_creative_id: string;
  dimensions: string | null;
  thumbnail_url: string | null;
  group_id: string | null;
  aspect_ratio_category: string | null;
  creative_type: string;
}

interface VerticalPairAsset {
  id: string;
  filename: string;
  meta_creative_id: string;
  thumbnail_url: string | null;
  group_id: string;
}

interface EnrichedAsset extends CreativeAsset {
  verticalPair: VerticalPairAsset | null;
}

interface CarouselCardEditorProps {
  isOpen: boolean;
  onClose: () => void;
  adAccountId: string;
  userId: string;
  existingCards: CarouselCard[];
  onSave: (cards: CarouselCard[]) => void;
  defaultCta?: string;
  defaultUrl?: string;
}

type AssetTypeFilter = "all" | "image" | "video";

function resolveAssetType(asset: { creative_type: string; filename: string }): "image" | "video" {
  if (asset.creative_type === "video") return "video";
  const ext = asset.filename.toLowerCase().split(".").pop() || "";
  if (["mp4", "mov", "webm"].includes(ext)) return "video";
  return "image";
}

export function CarouselCardEditor({
  isOpen,
  onClose,
  adAccountId,
  userId,
  existingCards,
  onSave,
  defaultCta,
  defaultUrl,
}: CarouselCardEditorProps) {
  const [page, setPage] = useState<1 | 2>(1);
  const [assets, setAssets] = useState<EnrichedAsset[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<AssetTypeFilter>("all");
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<CarouselCard[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pendingTypeSwitch, setPendingTypeSwitch] = useState<AssetTypeFilter | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Derive locked type from selected assets (works on Page 1) OR cards (Page 2)
  const lockedType = useMemo<"image" | "video" | null>(() => {
    // Check cards first (Page 2 / existing cards)
    if (cards.length > 0) {
      return cards[0].creative_type || "image";
    }
    // Check selected assets (Page 1)
    if (selectedAssetIds.size === 0) return null;
    const firstSelectedAsset = assets.find(a => selectedAssetIds.has(a.id));
    if (!firstSelectedAsset) return null;
    return resolveAssetType(firstSelectedAsset);
  }, [cards, selectedAssetIds, assets]);

  const isTypeLocked = lockedType !== null;

  // Effective filter: when locked, force filter to match; otherwise user selection
  const effectiveTypeFilter: AssetTypeFilter = isTypeLocked ? lockedType : typeFilter;

  // Initialize from existing cards when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (existingCards.length > 0) {
        setCards(existingCards.map(c => ({ ...c })));
        setSelectedAssetIds(new Set(existingCards.map(c => c.creative_id)));
        setPage(2);
      } else {
        setCards([]);
        setSelectedAssetIds(new Set());
        setPage(1);
      }
      setSearchQuery("");
      setTypeFilter("all");
    }
  }, [isOpen, existingCards]);

  // Fetch feed assets and their vertical pairs
  useEffect(() => {
    async function fetchAssets() {
      if (!adAccountId || !userId) return;
      setLoading(true);

      const { data: feedAssets, error } = await supabase
        .from("creative_metadata")
        .select("id, filename, meta_creative_id, dimensions, thumbnail_url, group_id, aspect_ratio_category, creative_type")
        .eq("user_id", userId)
        .eq("meta_account_id", adAccountId)
        .eq("aspect_ratio_category", "feed")
        .order("created_at", { ascending: false });

      if (error || !feedAssets) {
        console.error("[CarouselCardEditor] Error fetching assets:", error);
        setLoading(false);
        return;
      }

      const groupIds = [...new Set(feedAssets.filter(a => a.group_id).map(a => a.group_id!))];

      let verticalByGroup: Record<string, VerticalPairAsset> = {};
      if (groupIds.length > 0) {
        const { data } = await supabase
          .from("creative_metadata")
          .select("id, filename, meta_creative_id, thumbnail_url, group_id")
          .eq("user_id", userId)
          .eq("aspect_ratio_category", "vertical")
          .in("group_id", groupIds);

        (data || []).forEach((v: any) => {
          if (v.group_id) verticalByGroup[v.group_id] = v;
        });
      }

      setAssets(feedAssets.map(asset => ({
        ...asset,
        verticalPair: asset.group_id ? verticalByGroup[asset.group_id] || null : null,
      })));
      setLoading(false);
    }

    if (isOpen) fetchAssets();
  }, [adAccountId, userId, isOpen]);

  // Filter assets by search + effective type
  const filteredAssets = useMemo(() => {
    let result = assets;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(a => a.filename.toLowerCase().includes(q));
    }
    if (effectiveTypeFilter !== "all") {
      result = result.filter(a => resolveAssetType(a) === effectiveTypeFilter);
    }
    return result;
  }, [assets, debouncedSearch, effectiveTypeFilter]);

  const toggleAsset = useCallback((asset: EnrichedAsset) => {
    const assetType = resolveAssetType(asset);

    setSelectedAssetIds(prev => {
      const next = new Set(prev);
      if (next.has(asset.id)) {
        next.delete(asset.id);
        return next;
      }

      // Enforce type consistency: check against first selected asset
      if (prev.size > 0) {
        const firstSelected = assets.find(a => prev.has(a.id));
        if (firstSelected && resolveAssetType(firstSelected) !== assetType) {
          toast({ title: "All carousel cards must be the same media type", variant: "destructive" });
          return prev; // Return unchanged set
        }
      }

      if (next.size < 10) {
        next.add(asset.id);
      }
      return next;
    });
  }, [assets]);

  const handleNext = useCallback(() => {
    const selectedAssets = assets.filter(a => selectedAssetIds.has(a.id));

    const newCards: CarouselCard[] = selectedAssets.map((asset, index) => {
      const existingCard = cards.find(c => c.creative_id === asset.id);
      if (existingCard) return existingCard;

      return {
        id: `card_${Date.now()}_${index}`,
        creative_id: asset.id,
        creative_filename: asset.filename,
        meta_creative_id: asset.meta_creative_id,
        thumbnail_url: asset.thumbnail_url,
        headline: "",
        description: "",
        card_url: "",
        creative_filename_vertical: asset.verticalPair?.filename || null,
        meta_creative_id_vertical: asset.verticalPair?.meta_creative_id || null,
        creative_type: resolveAssetType(asset),
      };
    });

    setCards(newCards);
    setPage(2);
  }, [assets, selectedAssetIds, cards]);

  const handleBack = useCallback(() => {
    setPage(1);
  }, []);

  const updateCardField = useCallback((cardId: string, field: keyof CarouselCard, value: string | undefined) => {
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, [field]: value } : c));
  }, []);

  const removeCard = useCallback((cardId: string) => {
    setCards(prev => {
      const removedCard = prev.find(c => c.id === cardId);
      if (removedCard) {
        setSelectedAssetIds(prevIds => {
          const next = new Set(prevIds);
          next.delete(removedCard.creative_id);
          return next;
        });
      }
      return prev.filter(c => c.id !== cardId);
    });
  }, []);

  const moveCard = useCallback((index: number, direction: "up" | "down") => {
    setCards(prev => {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  }, []);

  const isValid = useMemo(() => {
    return cards.length >= 2 && cards.every(c => c.headline.trim().length > 0);
  }, [cards]);

  const handleSave = useCallback(() => {
    if (!isValid) {
      toast({ title: "All cards must have a headline", variant: "destructive" });
      return;
    }
    // Strip cta_type from cards — CTA is set at the ad row level
    const cleanedCards = cards.map(({ cta_type, ...rest }) => rest);
    onSave(cleanedCards as CarouselCard[]);
    onClose();
  }, [cards, isValid, onSave, onClose]);

  // Handle type filter change — prompt confirmation if cards exist
  const handleTypeFilterChange = useCallback((newType: AssetTypeFilter) => {
    if (isTypeLocked) {
      // Shouldn't happen since dropdown is disabled, but guard anyway
      return;
    }
    setTypeFilter(newType);
  }, [isTypeLocked]);

  // Clear & Switch confirmation
  const handleClearAndSwitch = useCallback(() => {
    if (pendingTypeSwitch) {
      setCards([]);
      setSelectedAssetIds(new Set());
      setTypeFilter(pendingTypeSwitch);
      setPendingTypeSwitch(null);
    }
  }, [pendingTypeSwitch]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              {page === 1 ? "Select Assets for Carousel" : "Configure Cards"}
            </span>
            <Badge variant="secondary" className="font-normal">
              {page === 1
                ? `${selectedAssetIds.size}/10 selected`
                : `${cards.length} cards`}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {page === 1 ? (
          <>
            {/* Filter Row */}
            <div className="flex items-center gap-3">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Select
                        value={effectiveTypeFilter}
                        onValueChange={(v) => handleTypeFilterChange(v as AssetTypeFilter)}
                        disabled={isTypeLocked}
                      >
                        <SelectTrigger className={cn("w-[130px]", isTypeLocked && "opacity-60 cursor-not-allowed")}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="image">Images</SelectItem>
                          <SelectItem value="video">Videos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TooltipTrigger>
                  {isTypeLocked && (
                    <TooltipContent>
                      <p>{cards.length > 0 ? "Clear all cards to switch media type" : "Deselect all assets to switch media type"}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search assets by filename..."
                  className="pl-9"
                />
              </div>
              {isTypeLocked && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => setPendingTypeSwitch("all")}
                >
                  Clear & Switch Type
                </Button>
              )}
            </div>

            {/* Assets Grid */}
            <div className="flex-1 overflow-y-auto max-h-[60vh] min-h-0">
              {loading ? (
                <div className="grid grid-cols-4 gap-3 p-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-muted animate-pulse rounded-lg" />
                  ))}
                </div>
              ) : filteredAssets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                  <ImageIcon className="h-12 w-12 mb-4" />
                  <p>No feed assets available</p>
                  <p className="text-sm">Upload 1:1 ratio images/videos to use in carousels</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3 p-2">
                  {filteredAssets.map((asset) => {
                    const isSelected = selectedAssetIds.has(asset.id);
                    const assetType = resolveAssetType(asset);
                    const isVideo = assetType === "video";
                    return (
                      <button
                        key={asset.id}
                        onClick={() => toggleAsset(asset)}
                        className={cn(
                          "relative aspect-square rounded-lg overflow-hidden border-2 transition-all group",
                          isSelected
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-transparent hover:border-muted-foreground/30"
                        )}
                      >
                        {asset.thumbnail_url ? (
                          <img
                            src={asset.thumbnail_url}
                            alt={asset.filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            {isVideo ? <Video className="h-8 w-8 text-muted-foreground" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
                          </div>
                        )}

                        {/* Selection checkbox */}
                        <div className={cn(
                          "absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                          isSelected
                            ? "bg-primary border-primary"
                            : "bg-background/80 border-muted-foreground"
                        )}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-primary-foreground" viewBox="0 0 12 12" fill="none">
                              <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>

                        {/* Vertical pair badge */}
                        {asset.verticalPair && (
                          <div className="absolute top-1 right-1 bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                            +Vertical
                          </div>
                        )}

                        {/* Video indicator */}
                        {isVideo && !asset.verticalPair && (
                          <div className="absolute top-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                            Video
                          </div>
                        )}

                        {/* Filename on hover */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-xs text-white truncate">{asset.filename}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleNext} disabled={selectedAssetIds.size < 2}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Card Configuration */}
            <ScrollArea className="flex-1 min-h-[400px] max-h-[500px]">
              <div className="space-y-3 p-2">
                {cards.map((card, index) => (
                  <div
                    key={card.id}
                    className={cn(
                      "flex items-start gap-3 p-3 bg-muted/50 rounded-lg border transition-colors",
                      !card.headline.trim() && "border-destructive/50",
                      card.headline.trim() && "hover:border-primary/30"
                    )}
                  >
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1 pt-2">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveCard(index, "up")} disabled={index === 0}>
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveCard(index, "down")} disabled={index === cards.length - 1}>
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Thumbnail */}
                    <div className="w-[60px] h-[60px] rounded-md overflow-hidden flex-shrink-0 bg-muted">
                      {card.thumbnail_url ? (
                        <img src={card.thumbnail_url} alt={card.creative_filename} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Card details */}
                    <div className="flex-1 space-y-2 min-w-0">
                      <div>
                        <p className="text-sm text-muted-foreground truncate">{card.creative_filename}</p>
                        {card.creative_filename_vertical && (
                          <p className="text-xs text-purple-600 truncate">+ Vertical: {card.creative_filename_vertical}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={card.headline}
                          onChange={(e) => updateCardField(card.id, "headline", e.target.value)}
                          placeholder="Enter headline (required)"
                          className={cn("h-9 text-sm", !card.headline.trim() && "border-destructive")}
                        />
                        <Input
                          value={card.description || ""}
                          onChange={(e) => updateCardField(card.id, "description", e.target.value)}
                          placeholder="Optional description"
                          className="h-9 text-sm"
                        />
                      </div>

                      <div>
                        <Input
                          value={card.card_url}
                          onChange={(e) => updateCardField(card.id, "card_url", e.target.value)}
                          placeholder="Leave empty to use main URL"
                          className="h-9 text-sm"
                        />
                        {defaultUrl && !card.card_url && (
                          <p className="text-[10px] text-muted-foreground mt-0.5 truncate">Fallback: {defaultUrl}</p>
                        )}
                      </div>
                    </div>

                    {/* Delete button */}
                    {cards.length > 2 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive shrink-0"
                        onClick={() => removeCard(card.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}

                {cards.length < 10 && (
                  <Button variant="outline" className="w-full" onClick={handleBack}>
                    <Plus className="h-4 w-4 mr-2" /> Add More Cards
                  </Button>
                )}
              </div>
            </ScrollArea>

            {/* Validation messages */}
            {!isValid && cards.length >= 2 && (
              <p className="text-sm text-destructive">All cards must have a headline</p>
            )}
            {cards.length < 2 && (
              <p className="text-sm text-destructive">Minimum 2 cards required</p>
            )}

            {/* Footer */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button onClick={handleSave} disabled={!isValid}>
                Save Cards
              </Button>
            </div>
          </>
        )}

        {/* Clear & Switch confirmation dialog */}
        <AlertDialog open={pendingTypeSwitch !== null} onOpenChange={(open) => !open && setPendingTypeSwitch(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Switch media type?</AlertDialogTitle>
              <AlertDialogDescription>
                Switching media type will remove all current cards. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleClearAndSwitch}>Clear & Switch</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
